import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import { HttpService } from '@core/services';
import { SOFToolHttpService } from '@core/services/http';
import { getIdParamFromUrl, removeEmptyFields } from '@core/utils/methods';
import { SOFToolOrderInfo, SOFToolOrderInfoData } from '@models/interfaces';
import { HeaderBar } from '@shared/components';
import {
  ProjectCategoriesSelectors,
  SOFToolSelectors,
  UserSelectors,
} from '@store/selectors';
import { SOFActionButtons, SOFServicesTable } from './components';
import { PreviewModal } from './modals';
import { SOF_TOOL_HEADER_SITE_OPTIONS } from './models';
import { hasData } from './validators';

import './styles.scss';

export const SOFToolDetails: FC = () => {
  const SOFToolService = useMemo(
    () => HttpService.getHttpRequests(SOFToolHttpService),
    [],
  );

  const details = useSelector(SOFToolSelectors.detailsData);
  const detailsHeaderData = useSelector(SOFToolSelectors.detailsHeaderData);
  const previewSrc = useSelector(SOFToolSelectors.previewSrc);
  const isFetching = useSelector(SOFToolSelectors.isFetching);
  const projectCategoriesTableVisible = useSelector(
    ProjectCategoriesSelectors.getProjectCategoriesTableVisible,
  );
  const projectType = useSelector(SOFToolSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );

  const [values, setValues] = useState<SOFToolOrderInfo>({
    orderDate: moment(),
    orderedBy: '',
    data: [],
    attachment: null,
  });
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);

  const configurationPanelChanges = (
    newValues: Partial<Omit<SOFToolOrderInfo, 'data'>>,
  ): void => {
    setValues((prevState: SOFToolOrderInfo) => ({
      ...prevState,
      ...newValues,
    }));
  };

  const chooseData = (
    checked: boolean,
    { servicesRequired, ...selectedRow }: SOFToolOrderInfoData,
  ): void => {
    setValues((prevState: SOFToolOrderInfo) => ({
      ...prevState,
      data: checked
        ? [...prevState.data, selectedRow]
        : [...prevState.data].filter(
            (SOFToolOrderInfoDataItem: SOFToolOrderInfoData) =>
              SOFToolOrderInfoDataItem.id !== selectedRow.id,
          ),
    }));
  };

  const makePreview = async (): Promise<void> => {
    const [projectId] = getIdParamFromUrl();

    const { data, orderDate, orderedBy } = values;

    await SOFToolService.previewPDF(projectId, {
      data,
      orderDate,
      orderedBy,
    });

    setPreviewVisible(!previewVisible);
  };

  const send = async (): Promise<void> => {
    const [projectId] = getIdParamFromUrl();

    const { data, orderDate, orderedBy, attachment } = values;

    await SOFToolService.sendPDF(
      projectId,
      removeEmptyFields({
        attachment,
        orderData: JSON.stringify({
          orderDate,
          orderedBy,
          data,
        }),
      }),
    );
  };

  return (
    <div className="prov-sof-tool-details">
      <HeaderBar
        site={detailsHeaderData.site}
        siteOptions={SOF_TOOL_HEADER_SITE_OPTIONS}
        showTowerOwner={false}
        withoutTabPanel
      />
      <div
        className={classNames('scrolling-wrap', {
          'categories-table-open': projectCategoriesTableVisible,
        })}
      >
        <SOFServicesTable
          detailsData={details}
          values={values}
          configurationPanelChanges={configurationPanelChanges}
          chooseData={chooseData}
          isFetching={isFetching}
          projectTypePermission={projectTypePermission}
        />
      </div>
      <SOFActionButtons
        send={send}
        preview={makePreview}
        disabled={!isFetching ? hasData(values) : isFetching}
        cancelDisabled={isFetching}
      />
      <PreviewModal
        visible={previewVisible}
        src={previewSrc}
        onClose={(): void => setPreviewVisible(!previewVisible)}
      />
    </div>
  );
};
